import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, Lock, User, ArrowRight, Chrome, Sun, Moon } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useTheme } from '../theme/ThemeContext';
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from '../constants/legalLinks';
import { sendGoogleAdsConversion } from '../utils/googleAds';

function Signup() {
  const navigate = useNavigate();
  const { signup, googleLogin, isAuthenticated, loading: authLoading } = useAuth();
  const { theme, darkMode, toggleTheme } = useTheme();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Redirect to profile when authenticated (already logged in, or just finished signup)
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      // If we just showed the confirmation message, still go to profile after a short delay so user sees it
      if (confirmMessage) {
        const t = setTimeout(() => navigate('/profile', { replace: true }), 3000);
        return () => clearTimeout(t);
      }
      navigate('/profile', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, confirmMessage]);

  const capitalizeName = (s) => (s || '').trim().split(/\s+/).filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ') || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) return;
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setError('');
    setConfirmMessage('');
    setLoading(true);

    try {
      const result = await signup(email, password, capitalizeName(name));
      if (result.success) {
        sendGoogleAdsConversion('signup', { dedupeKey: `signup:${email.trim().toLowerCase()}`, email });
        if (result.needsConfirmation) {
          setConfirmMessage('Account created. Please check your email to confirm your address.');
        }
        // Otherwise navigation happens in useEffect when isAuthenticated becomes true
      }
    } catch (err) {
      setError('Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    setError('');
    try {
      sessionStorage.setItem('marketscan_pending_google_signup', 'true');
      await googleLogin();
    } catch (err) {
      setError('Google signup failed');
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: theme.bg, color: theme.text, display: 'flex', flexDirection: 'column' }}>
      <nav style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '16px 24px',
        borderBottom: `1px solid ${theme.border}`,
        backgroundColor: theme.card,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => navigate('/')}>
          <img src="/logo.png" alt="Market Scan" style={{ width: 128, height: 128, objectFit: 'contain', borderRadius: '12px', display: 'block', backgroundColor: 'transparent' }} />
        </div>
        <button
          type="button"
          onClick={toggleTheme}
          style={{
            padding: '8px',
            backgroundColor: theme.primaryLight,
            color: theme.primary,
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title={darkMode ? 'Light mode' : 'Dark mode'}
        >
          {darkMode ? <Sun size={20} /> : <Moon size={20} />}
        </button>
      </nav>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{
          width: '100%',
          maxWidth: '420px',
          padding: '32px 24px',
          backgroundColor: theme.card,
          borderRadius: '16px',
          boxShadow: theme.shadow,
          border: `1px solid ${theme.border}`,
          boxSizing: 'border-box',
        }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '24px' }}>
            <img src="/logo.png" alt="Market Scan" style={{ width: 96, height: 96, objectFit: 'contain', borderRadius: '12px', display: 'block', backgroundColor: 'transparent' }} />
          </div>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: theme.text, textAlign: 'center', margin: '0 0 8px 0' }}>Start your free trial</h1>
          <p style={{ fontSize: '14px', color: theme.textMuted, textAlign: 'center', margin: '0 0 24px 0' }}>Join thousands of market researchers</p>
          {error && (
            <div style={{ padding: '12px', backgroundColor: theme.errorBg, border: `1px solid ${theme.errorBorder}`, borderRadius: '8px', color: theme.errorText, fontSize: '14px', marginBottom: '20px' }}>
              {error}
            </div>
          )}
          {confirmMessage && (
            <div style={{ padding: '12px', backgroundColor: theme.primaryLight, border: `1px solid ${theme.primary}`, borderRadius: '8px', color: theme.text, fontSize: '14px', marginBottom: '20px' }}>
              {confirmMessage} You can go to your profile and we&apos;ll remind you there.
            </div>
          )}
          <button
            type="button"
            onClick={handleGoogleSignup}
            style={{
              width: '100%',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              backgroundColor: theme.inputBg,
              border: `1px solid ${theme.inputBorder}`,
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: theme.text,
              cursor: 'pointer',
              marginBottom: '8px',
            }}
          >
            <Chrome size={20} />
            Continue with Google
          </button>
          <p style={{ fontSize: '12px', color: theme.textMuted, textAlign: 'center', margin: '0 0 20px 0' }}>
            By continuing you agree to our <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noopener noreferrer" style={{ color: theme.primary, textDecoration: 'underline' }}>Terms</a> and <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" style={{ color: theme.primary, textDecoration: 'underline' }}>Privacy Policy</a>.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: theme.border }} />
            <span style={{ fontSize: '14px', color: theme.textMuted }}>or</span>
            <div style={{ flex: 1, height: '1px', backgroundColor: theme.border }} />
          </div>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: theme.text, marginBottom: '6px' }}>Full Name</label>
              <div style={{ position: 'relative' }}>
                <User size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: theme.textMuted }} />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  required
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '10px 12px 10px 40px',
                    fontSize: '14px',
                    border: `1px solid ${theme.inputBorder}`,
                    borderRadius: '8px',
                    outline: 'none',
                    backgroundColor: theme.inputBg,
                    color: theme.text,
                  }}
                />
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: theme.text, marginBottom: '6px' }}>Email</label>
              <div style={{ position: 'relative' }}>
                <Mail size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: theme.textMuted }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '10px 12px 10px 40px',
                    fontSize: '14px',
                    border: `1px solid ${theme.inputBorder}`,
                    borderRadius: '8px',
                    outline: 'none',
                    backgroundColor: theme.inputBg,
                    color: theme.text,
                  }}
                />
              </div>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: theme.text, marginBottom: '6px' }}>Password</label>
              <div style={{ position: 'relative' }}>
                <Lock size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: theme.textMuted }} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '10px 12px 10px 40px',
                    fontSize: '14px',
                    border: `1px solid ${theme.inputBorder}`,
                    borderRadius: '8px',
                    outline: 'none',
                    backgroundColor: theme.inputBg,
                    color: theme.text,
                  }}
                />
              </div>
              <p style={{ fontSize: '12px', color: theme.textMuted, margin: '6px 0 0 0' }}>Must be at least 8 characters</p>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: theme.text, marginBottom: '6px' }}>Confirm Password</label>
              <div style={{ position: 'relative' }}>
                <Lock size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: theme.textMuted }} />
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '10px 12px 10px 40px',
                    fontSize: '14px',
                    border: `1px solid ${confirmPassword && confirmPassword !== password ? '#ef4444' : theme.inputBorder}`,
                    borderRadius: '8px',
                    outline: 'none',
                    backgroundColor: theme.inputBg,
                    color: theme.text,
                  }}
                />
              </div>
              {confirmPassword && confirmPassword !== password && (
                <p style={{ fontSize: '12px', color: '#ef4444', margin: '6px 0 0 0' }}>Passwords do not match</p>
              )}
            </div>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '20px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                style={{ marginTop: '4px', width: '18px', height: '18px', accentColor: theme.primary }}
              />
              <span style={{ fontSize: '13px', color: theme.text }}>
                I agree to the{' '}
                <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noopener noreferrer" style={{ color: theme.primary, textDecoration: 'underline' }}>Terms of Service</a>
                {' '}and{' '}
                <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" style={{ color: theme.primary, textDecoration: 'underline' }}>Privacy Policy</a>
              </span>
            </label>
            <button
              type="submit"
              disabled={loading || !agreedToTerms || password !== confirmPassword}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: (loading || !agreedToTerms || password !== confirmPassword) ? theme.primaryMuted : theme.primary,
                color: theme.primaryContrast,
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: (loading || !agreedToTerms || password !== confirmPassword) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              {loading ? 'Creating account...' : (<>Create Account <ArrowRight size={16} /></>)}
            </button>
          </form>
          <p style={{ fontSize: '14px', color: theme.textMuted, textAlign: 'center', margin: '24px 0 0 0' }}>
            Already have an account? <Link to="/login" style={{ color: theme.primary, textDecoration: 'none', fontWeight: '600' }}>Sign in</Link>
          </p>
          <p style={{ fontSize: '12px', color: theme.textMuted, textAlign: 'center', marginTop: '24px' }}>
            By creating an account you agree to our{' '}
            <a href={TERMS_OF_SERVICE_URL} target="_blank" rel="noopener noreferrer" style={{ color: theme.primary, textDecoration: 'none' }}>Terms of Service</a>
            {' '}and{' '}
            <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" style={{ color: theme.primary, textDecoration: 'none' }}>Privacy Policy</a>.
          </p>
          <p style={{ fontSize: '12px', color: theme.textMuted, textAlign: 'center', marginTop: '12px' }}>
            <Link to="/" style={{ color: theme.textMuted, textDecoration: 'none' }}>← Back to home</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Signup;
